import type { NativeSelectOption } from '@shared/ui/fields'

import { ReasonCategory, Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'

/** The `change_status_reasons` list: reasons grouped by who caused the change. */
export type LessonStatusReasons = Partial<Record<string, Record<string, string>>>

const RESERVED_STATUSES: readonly BookingStatus[] = [BookingStatus.ReservedPlanned, BookingStatus.ReservedConfirmed]

/** The statuses that need a reason category (the API refuses them without one) and show the reason fields. */
const LESSON_REASON_STATUSES: readonly BookingStatus[] = [
  BookingStatus.Canceled,
  BookingStatus.CanceledBad,
  BookingStatus.Rescheduled,
  BookingStatus.RescheduledBad,
  BookingStatus.PendingRescheduled,
  BookingStatus.NoShow,
]

export const LESSON_CANCEL_STATUSES: readonly BookingStatus[] = [BookingStatus.Canceled, BookingStatus.CanceledBad]

/** The reschedule endpoint accepts only these two statuses for the old lesson. */
export const LESSON_RESCHEDULE_STATUSES: readonly BookingStatus[] = [BookingStatus.Rescheduled, BookingStatus.RescheduledBad]

export const isLessonReasonStatus = (status: BookingStatus | null): boolean =>
  !!status && LESSON_REASON_STATUSES.includes(status)

/** A canceled or no-show lesson keeps its date, time and tutor: the legacy form locked them. */
export const isLessonTimeLocked = (status: BookingStatus | null): boolean =>
  !!status && [...LESSON_CANCEL_STATUSES, BookingStatus.NoShow].includes(status)

/** A reserved lesson moves only between the reserved statuses, any other lesson never into them. */
export const getLessonStatusChoices = (labels: Record<string, string>, currentStatus: BookingStatus): BookingStatus[] => {
  const isReserved = RESERVED_STATUSES.includes(currentStatus)

  return (Object.keys(labels) as BookingStatus[])
    .filter(status => RESERVED_STATUSES.includes(status) === isReserved)
}

export const buildLessonStatusOptions = (
  labels: Record<string, string>,
  statuses: readonly BookingStatus[],
  selected: BookingStatus | null,
): NativeSelectOption[] =>
  statuses.map(status => ({
    id: status,
    value: status,
    text: labels[status] ?? status,
    selected: status === selected,
  }))

/** "All" only groups the other categories and "System" is set by the platform, so neither can be picked. */
export const buildReasonCategoryOptions = (
  reasons: LessonStatusReasons,
  labels: Record<string, string>,
  selected: string | null,
): NativeSelectOption[] =>
  Object.keys(reasons).map(category => ({
    id: category,
    value: category,
    text: labels[category] ?? category,
    selected: category === selected,
    disabled: category === ReasonCategory.System,
    hidden: category === ReasonCategory.All,
  }))

export const buildReasonOptions = (
  reasons: LessonStatusReasons,
  category: string | null,
  selected: string | null,
): NativeSelectOption[] =>
  Object.entries((category && reasons[category]) || {}).map(([value, text]) => ({
    id: value,
    value,
    text,
    selected: value === selected,
  }))

export const getFirstReason = (reasons: LessonStatusReasons, category: string | null): string | null =>
  Object.keys((category && reasons[category]) || {})[0] ?? null

/** The legacy list nests the tutor change reasons by audience; the admin ones are offered here. */
export const getTutorReassignReasons = (reasons: Record<string, unknown> | undefined): Record<string, string> => {
  const admin = reasons?.admin

  if (admin && typeof admin === 'object') return admin as Record<string, string>

  return Object.fromEntries(Object.entries(reasons ?? {}).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
}
